export function normalizeToolName(name: string): string {
  return name.replace(/^mcp__(?:ivy|slide-tools|plugin_ivy_ivy)__/, '');
}

const DISPLAY_NAMES: Record<string, string> = {
  compile_slide: 'Rendering scene',
  sync_slide_to_production: 'Publishing scene',
  create_video_project: 'Setting up project',
  prepare_workspace: 'Preparing canvas',
  prepare_slide_workspace_local: 'Preparing canvas',
  update_outline: 'Updating storyboard',
  validate_plan_layouts: 'Checking layouts',
  build_video_manifest: 'Assembling video',
  get_video_url: 'Getting video link',
  generate_image: 'Creating visual',
  generate_character: 'Creating character',
  generate_cutout: 'Creating cutout',
  stock_photo_search: 'Finding photos',
  stock_video_search: 'Finding video clips',
  icon_search: 'Finding icons',
  lottie_search: 'Finding motion graphics',
  safe_write_file: 'Designing',
  safe_edit_file: 'Refining design',
  safe_read_file: 'Reviewing scene',
  batch_replace: 'Updating content',
  generate_tts: 'Recording narration',
  generate_tts_all: 'Recording all narration',
  generate_batch_tts: 'Recording narration',
  apply_audio: 'Adding audio',
  generate_slide_gemini: 'AI-designing scene',
  cache_media: 'Uploading media',
  set_project_template: 'Applying design system',
  get_template_capabilities: 'Loading design options',
  get_slide_generation_context: 'Loading creative brief',
  list_projects: 'Browsing projects',
  get_project: 'Loading project',
  get_outline: 'Loading storyboard',
  browse_slides: 'Browsing scenes',
  copy_slide: 'Copying scene',
  download_slide_source: 'Loading scene source',
  upload_resource: 'Uploading file',
  transcribe_audio: 'Transcribing audio',
  authenticate: 'Connecting',
  list_slide_versions: 'Checking versions',
  fetch_previous_version: 'Loading previous version',
  diff_slide_versions: 'Comparing versions',
  stock_data: 'Getting market data',
  market_discovery: 'Researching market',
  get_slide_preview_url: 'Loading preview',
  capture_slide_preview: 'Capturing preview',
  show_video: 'Showing video',
  approve_outline: 'Approving storyboard',
  set_background_music: 'Adding music',
  set_video_program: 'Composing the program',
  build_video_program: 'Joining the scenes into one program',
  set_video_handoff: 'Designing a scene handoff',
  browse_music_library: 'Browsing music',
  set_master_narration: 'Setting master audio',
  extract_website_branding: 'Extracting branding',
  share_project: 'Sharing project',
  set_slide_duration: 'Adjusting timing',
  get_resource_status: 'Checking resources',
  resolve_timestamps: 'Syncing to audio',
  create_video_project_v2: 'Setting up project',
  Read: 'Reviewing',
  Write: 'Saving',
  Edit: 'Refining',
  Bash: 'Processing',
  Glob: 'Scanning files',
  Grep: 'Searching',
  Agent: 'Creative specialist',
  narrate_project: 'Recording narration',
  prepare_slide_workspaces: 'Preparing scenes',
  prepare_slide_workspace: 'Preparing canvas',
  sync_storyboard: 'Publishing storyboard',
  validate_storyboard: 'Checking storyboard',
  publish_video: 'Making a share link',
  republish_video: 'Updating the share link',
  export_video: 'Exporting MP4',
  capture_slide_screenshots: 'Checking frames',
  check_playback: 'Watching it through',
  get_storyboard_input: 'Reading your storyboard changes',
  apply_storyboard_input: 'Applying your storyboard changes',
  Task: 'Specialist',
  Skill: 'Running workflow',
  ToolSearch: 'Loading tools',
  WebSearch: 'Researching',
  WebFetch: 'Fetching reference',
};

const AGENT_TYPE_NAMES: Record<string, string> = {
  'general-purpose': 'Research specialist',
  'Explore': 'Explorer',
  'Plan': 'Architect',
  'slide-builder': 'Slide designer',
  'slide-editor': 'Slide editor',
  'chart-specialist': 'Chart specialist',
  'animation-choreographer': 'Animation director',
  'content-planner': 'Content planner',
  'scene-director': 'Scene director',
  'image-generator': 'Image creator',
  'outline_planner': 'Outline planner',
  'image-to-slide': 'Design recreator',
  'icon-manager': 'Icon finder',
  'lottie-manager': 'Animation finder',
};

export function toolDisplayName(name: string, input?: Record<string, unknown>): string {
  const normalized = normalizeToolName(name);
  if (normalized === 'Agent' && input?.subagent_type) {
    return AGENT_TYPE_NAMES[input.subagent_type as string] || (input.subagent_type as string).replace(/[-_]/g, ' ');
  }
  return DISPLAY_NAMES[normalized] || normalized.replace(/_/g, ' ');
}

const IRREGULAR: Record<string, string> = { Setting: 'Set', Running: 'Ran', Making: 'Made', Finding: 'Found', Getting: 'Got', Reading: 'Read', Building: 'Built', Writing: 'Wrote' };

/** The display name once the step is done: its first word in the past tense ("Rendering scene" → "Rendered scene"). */
export function toolDoneName(name: string, input?: Record<string, unknown>): string {
  const display = toolDisplayName(name, input);
  const [first, ...rest] = display.split(' ');
  if (!first.endsWith('ing')) return display;
  const stem = first.slice(0, -3);
  const done = IRREGULAR[first] ?? (first.endsWith('ying') ? `${first.slice(0, -4)}ied` : stem.endsWith('e') ? `${stem}d` : `${stem}ed`);
  return [done, ...rest].join(' ');
}

export function toolSummary(name: string, input: Record<string, unknown>): string {
  const normalized = normalizeToolName(name);

  // Map file paths to creative labels
  const fileLabel = (p: string): string => {
    const name = p.split('/').pop() || '';
    if (name === 'template.html') return 'scene layout';
    if (name === 'styles.css') return 'visual styling';
    if (name === 'animations.json') return 'motion choreography';
    if (name === 'script.json') return 'narration script';
    if (name === 'chart_config.json') return 'chart design';
    if (name === 'plan.json') return 'storyboard';
    if (name === 'resource-manifest.json') return 'uploaded resources';
    return name;
  };

  switch (normalized) {
    case 'compile_slide':
      return `Scene ${input.outlineSlideId || input.slideId || ''}`;
    case 'sync_slide_to_production':
      return `Scene ${input.outlineSlideId || input.slideId || ''}`;
    case 'capture_slide_preview':
      return `Scene ${input.outlineSlideId || input.slideId || ''}`;
    case 'build_video_manifest':
      return '';
    case 'get_video_url':
      return '';
    case 'show_video':
      return '';
    case 'generate_image':
      return `"${String(input.prompt || '').slice(0, 60)}"`;
    case 'generate_character':
      return input.descriptor ? `"${String(input.descriptor).slice(0, 60)}"` : '';
    case 'generate_cutout':
      return input.subject ? `"${String(input.subject).slice(0, 60)}"` : '';
    case 'narrate_project':
      return String(input.voiceId || '');
    case 'update_outline': {
      const outline = input.outline as Record<string, unknown> | undefined;
      const slides = outline?.slides as unknown[] | undefined;
      return slides ? `${slides.length} scenes` : '';
    }
    case 'create_video_project':
    case 'create_video_project_v2':
      return String(input.title || input.name || '').slice(0, 60);
    case 'safe_write_file':
    case 'safe_edit_file':
    case 'safe_read_file':
      return fileLabel(String(input.filePath || input.path || ''));
    case 'stock_photo_search':
    case 'stock_video_search':
    case 'icon_search':
    case 'lottie_search':
      return `"${String(input.query || '').slice(0, 60)}"`;
    case 'generate_tts':
      return input.outlineSlideId || input.slideId ? `Scene ${input.outlineSlideId || input.slideId}` : '';
    case 'cache_media':
      return String(input.filename || '').slice(0, 40) || '';
    case 'Read':
      return fileLabel(String(input.file_path || ''));
    case 'Write':
      return fileLabel(String(input.file_path || ''));
    case 'Edit':
      return fileLabel(String(input.file_path || ''));
    case 'Bash': {
      const cmd = String(input.command || '');
      if (cmd.startsWith('ffmpeg')) return 'Processing video';
      if (cmd.startsWith('ffprobe')) return 'Analyzing media';
      if (cmd.startsWith('yt-dlp')) return 'Downloading video';
      if (cmd.startsWith('curl')) return 'Fetching data';
      return '';
    }
    case 'Glob':
      return '';
    case 'Grep':
      return '';
    case 'Agent':
      return String(input.description || '').slice(0, 60);
    case 'Skill':
      return String(input.skill || '').replace(/-/g, ' ');
    case 'ToolSearch':
      return '';
    case 'WebSearch':
      return `"${String(input.query || '').slice(0, 60)}"`;
    default:
      return defaultSummary(input);
  }
}

function defaultSummary(input: Record<string, unknown>): string {
  if (input.command) return String(input.command).slice(0, 80);
  if (input.file_path) return String(input.file_path);
  if (input.filePath) return String(input.filePath);
  if (input.pattern) return `pattern: ${input.pattern}`;
  if (input.query) return String(input.query).slice(0, 80);
  if (input.prompt) return String(input.prompt).slice(0, 80);
  if (input.description) return String(input.description).slice(0, 80);
  return '';
}
